import { useState, useRef, useCallback, MutableRefObject } from "react"

type Init<T> = NotFunction<T> | (() => T)
type Update<T> = NotFunction<T> | ((prev: T) => T)
type NotFunction<T> = T extends Function ? never : T

/**
 * `useState` with an included ref, so state can be more easily reused in
 * callbacks and effects without the stale closure problem.
 */

export function useStateWithRef<T>(init: Init<T>): [T, (update: Update<T>) => void, MutableRefObject<T>] {
  const initialValue = init instanceof Function ? init() : init
  const [state, setState] = useState<T>(initialValue)
  const ref = useRef<T>(initialValue)

  const setStateAndRef = useCallback(function (update: Update<T>) {
    ref.current = update instanceof Function ? update(ref.current) : update
    setState(ref.current)
  }, [])

  return [state, setStateAndRef, ref]
}
